<template>
  <b-form-group class="mb-3" label="CPF / CNPJ">
    <b-form-input
      type="text"
      :state="computedState"
      :class="inputClass"
      :value="inputValue"
      maxlength="18"
      @input="onInput"
      :placeholder="placeholder"
    />
    <b-form-invalid-feedback v-if="showRequiredError">
      CPF / CNPJ é obrigatório.
    </b-form-invalid-feedback>
    <b-form-invalid-feedback v-else-if="!computedState">
      {{ errorMessage }}
    </b-form-invalid-feedback>
  </b-form-group>
</template>

<script>
export default {
  props: {
    inputClass: {
      type: [String, Array, Object],
      default: ""
    },
    value: {
      type: [String, Number],
      default: ""
    },
    placeholder: {
      type: String,
      default: ""
    },
    errorMessage: {
      type: String,
      default: "CPF / CNPJ inválido."
    },
    state: {
      type: Boolean,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    submitted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      internalIsValid: null,
      inputValue: this.formatarDocumento(this.value)
    };
  },
  watch: {
    value(newValue) {
      this.inputValue = this.formatarDocumento(newValue);
    },
    state(newState) {
      if (newState !== null) {
        this.internalIsValid = newState;
      }
    },
    submitted(newVal) {
      if (newVal && this.required) {
        this.validateEmptyField();
      }
    }
  },
  computed: {
    computedState() {
      if (this.state !== null && this.state !== undefined) {
        return this.state;
      }
      return this.internalIsValid;
    },
    showRequiredError() {
      return (
        this.submitted &&
        this.required &&
        (!this.inputValue || this.inputValue.trim() === "")
      );
    }
  },
  methods: {
    onInput(value) {
      if (this.limparAlfanumerico(value).length > 14) {
        this.$forceUpdate();
        return;
      }

      this.inputValue = this.formatarDocumento(value);
      this.$emit("input", this.inputValue);
      this.handleInput(this.inputValue);
    },

    formatarDocumento(value) {
      const documento = this.limparAlfanumerico(value);
      const isCnpj = documento.length > 11 || /[A-Z]/.test(documento);

      if (!isCnpj) {
        return documento
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
      }

      const base = documento.substring(0, 12);
      const dv = documento.substring(12, 14);
      let formatado = base.substring(0, 2);

      if (base.length > 2) formatado += `.${base.substring(2, 5)}`;
      if (base.length > 5) formatado += `.${base.substring(5, 8)}`;
      if (base.length > 8) formatado += `/${base.substring(8, 12)}`;
      if (dv) formatado += `-${dv}`;

      return formatado;
    },

    handleInput(value) {
      if (!value || value.trim() === "") {
        this.internalIsValid = null;
        this.$emit("update:isValid", null);
        return;
      }

      this.internalIsValid = this.isValidCpfCnpj(value || this.inputValue);
      this.$emit("update:isValid", this.internalIsValid);
    },

    validateEmptyField() {
      if (
        this.required &&
        (!this.inputValue || this.inputValue.trim() === "")
      ) {
        this.internalIsValid = false;
        this.$emit("update:isValid", false);
      }
    },

    isValidCpfCnpj(value) {
      if (typeof value !== "string" && typeof value !== "number") return false;
      const cleanValue = this.limparAlfanumerico(value);

      if (cleanValue.length === 11) {
        return this.isValidCpf(cleanValue);
      } else if (cleanValue.length === 14) {
        return this.isValidCnpj(cleanValue);
      }
      return false;
    },

    limparAlfanumerico(value) {
      return String(value || "").toUpperCase().replace(/[^0-9A-Z]/g, "");
    },

    isValidCpf(cpf) {
      cpf = cpf.replace(/[^\d]+/g, "");
      if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

      let soma = 0;
      let resto;

      for (let i = 1; i <= 9; i++)
        soma += parseInt(cpf.substring(i - 1, i)) * (11 - i);
      resto = (soma * 10) % 11;
      if (resto === 10 || resto === 11) resto = 0;
      if (resto !== parseInt(cpf.substring(9, 10))) return false;

      soma = 0;
      for (let i = 1; i <= 10; i++)
        soma += parseInt(cpf.substring(i - 1, i)) * (12 - i);
      resto = (soma * 10) % 11;
      if (resto === 10 || resto === 11) resto = 0;
      if (resto !== parseInt(cpf.substring(10, 11))) return false;

      return true;
    },

    isValidCnpj(cnpj) {
      cnpj = this.limparAlfanumerico(cnpj);
      if (
        !/^[A-Z0-9]{12}\d{2}$/.test(cnpj) ||
        /^(\d)\1{13}$/.test(cnpj)
      ) {
        return false;
      }

      const base = cnpj.substring(0, 12);
      const digitos = cnpj.substring(12);

      const calcularDigito = chars => {
        const soma = [...chars].reduce((total, caractere, indice) => {
          const peso = ((chars.length - 1 - indice) % 8) + 2;
          return total + (caractere.charCodeAt(0) - 48) * peso;
        }, 0);
        const resto = soma % 11;

        return resto < 2 ? 0 : 11 - resto;
      };

      if (calcularDigito(base) !== Number(digitos.charAt(0))) {
        return false;
      }

      return calcularDigito(base + digitos.charAt(0)) === Number(digitos.charAt(1));
    }
  }
};
</script>

<style lang="scss" scoped>
.form-control {
  height: 32px;
}

.form-control::placeholder {
  height: 32px;
  opacity: 0.4;
}
</style>
